'use client'

import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import type { Locale } from '@susu/headless-commerce/config/locale'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useCurrencies } from '@susu/headless-commerce/contexts/currencies/client'
import type { NavigationCard } from '@susu/headless-commerce/features/navigation/domain/NavigationCard'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { createRenderNodeForPriceInfo } from '@susu/headless-commerce/utils/contentfulUtils'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackNavLinkClick } from '@susu/headless-commerce/utils/tracking/trackNavLinkClick'
import { isUndefined } from 'packages/undefined/dist'
import { ClientNavCard } from './ClientNavCard'
import styles from './NavCard.module.scss'

export type NavCardCardProps = {
  item: NavigationCard
  locale: Locale
}

export const NavCard = ({ item, locale }: NavCardCardProps) => {
  const { titleRich, description } = item
  const country = useCountry()
  const currencies = useCurrencies()
  const pageType = usePageType()

  const { locationId } = URLData()

  const url = item.link.relativeUrl ?? item.link.absoluteUrl
  const { trackingMenuIdValue, trackingParentId } = item

  const descriptionPrice = documentToReactComponents(
    description?.json,
    createRenderNodeForPriceInfo(
      description,
      country.ecommerce.currencyCode,
      country,
      currencies,
    ),
  )

  const titlePrice = documentToReactComponents(
    titleRich?.json,
    createRenderNodeForPriceInfo(
      titleRich,
      country.ecommerce.currencyCode,
      country,
      currencies,
    ),
  )

  const handleClick = () => {
    if (isUndefined(url)) {
      return
    }

    trackNavLinkClick({
      country,
      isSubMenuLink: false,
      locale,
      locationId,
      pageType,
      url,
      trackingMenuIdValue,
      trackingParentId,
    })
  }

  return (
    <div className={styles['nav__card']} onClick={handleClick}>
      <a
        className={styles['nav__card-link']}
        href={url}
        data-testid={item.link?.automationId}
      >
        <div className={styles['nav__card-image']}>
          <ClientNavCard item={item} />
        </div>
        <span className={styles['nav__card-title']}>{titlePrice}</span>
        <div className={styles['nav__card-description']}>
          {descriptionPrice}
        </div>
      </a>
    </div>
  )
}
